import ky, { type KyInstance, type Options, type ResponsePromise } from 'ky'
import type { IHttpClient, IHttpRequestSettings } from '../Contracts/Services/IHttpClient'
import { configManager } from '../Managers/Config'
import { BaseHttpClient } from './BaseHttpClient'
import { HTTPResponseStore } from './RestClientService/HTTPResponseStore'
import { requestProfiler } from './RestClientService/RequestProfiler'

interface RestClientServiceProps {
    backendUrl?: string
}

/**
 * Тип для методів Ky
 */
type KyMethod = KyInstance['get'] | KyInstance['post'] | KyInstance['put'] | KyInstance['patch'] | KyInstance['delete']

/**
 * Сервіс для роботи з REST API
 * Розширює BaseHttpClient та імплементує IHttpClient
 *
 * @description
 * RestClientService - низькорівневий HTTP клієнт, який:
 * - Обгортає бібліотеку Ky для зручної роботи з HTTP
 * - Надає підтримку Promise та MobX Store запитів
 * - Управляє кешуванням GET запитів
 * - Автоматично обробляє авторизацію та оновлення токенів
 * - Профілює запити у dev режимі
 * - Підтримує трансформери для запитів та відповідей
 *
 * @extends BaseHttpClient - отримує спільну логіку для всіх HTTP клієнтів
 * @implements IHttpClient - забезпечує єдиний інтерфейс
 *
 * @example
 * ```typescript
 * // Promise-based запит
 * const data = await restClient.get('/api/users')
 *
 * // Store-based запит з MobX реактивністю
 * const store = restClient.getStore('/api/users')
 * observe(() => {
 *   if (store.isLoading) return 'Loading...'
 *   return store.content
 * })
 * ```
 */
export class RestClientService
    extends BaseHttpClient<IHttpRequestSettings>
    implements IHttpClient<IHttpRequestSettings>
{
    /** Мапа с активными AbortController для отмены запросов */
    runningRequest: Map<string, AbortController> = new Map()
    
    /** Мапа с кэшированными HTTPResponseStore для повторного использования */
    cacheResponse: Map<string, HTTPResponseStore> = new Map()
    
    /** Мапа с активными промисами для дедупликации параллельных запитів (Store) */
    pendingRequests: Map<string, HTTPResponseStore> = new Map()
    
    /** Мапа з кешованими промисами для Promise-based GET запитів */
    private sharedPromises: Map<string, Promise<any>> = new Map()

    constructor(props?: RestClientServiceProps) {
        super(props?.backendUrl)
    }

    /**
     * Стандартні методи, які повертають Promise з KY client
     */

    /**
     * Виконує GET запит і повертає Promise
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns Promise з відповіддю від сервера
     */
    get<T = unknown>(url: string, options?: Options, settings?: IHttpRequestSettings): ResponsePromise<T> | null {
        return this.custom(ky.get, url, options, settings)
    }

    /**
     * Виконує POST запит і повертає Promise
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns Promise з відповіддю від сервера
     */
    post<T = unknown>(url: string, options?: Options, settings?: IHttpRequestSettings): ResponsePromise<T> | null {
        return this.custom(ky.post, url, options, settings)
    }

    /**
     * Виконує PUT запит і повертає Promise
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns Promise з відповіддю від сервера
     */
    put<T = unknown>(url: string, options?: Options, settings?: IHttpRequestSettings): ResponsePromise<T> | null {
        return this.custom(ky.put, url, options, settings)
    }

    /**
     * Виконує PATCH запит з автоматичним Content-Type і повертає Promise
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns Promise з відповіддю від сервера
     */
    patch<T = unknown>(url: string, options?: Options, settings?: IHttpRequestSettings): ResponsePromise<T> | null {
        const patchOptions = options ?? {}

        // Безпечна робота з headers
        if (!patchOptions.headers) {
            patchOptions.headers = {}
        }

        const headers = patchOptions.headers as Record<string, string>
        if (headers['Content-Type'] === undefined) {
            headers['Content-Type'] = 'application/merge-patch+json'
        }

        return this.custom(ky.patch, url, patchOptions, settings)
    }

    /**
     * Виконує DELETE запит з відповідними налаштуваннями і повертає Promise
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns Promise з відповіддю від сервера
     */
    delete<T = unknown>(url: string, options?: Options, settings?: IHttpRequestSettings): ResponsePromise<T> | null {
        const deleteSettings = settings ?? {}
        deleteSettings.isNotHeaderAddContentType = true
        deleteSettings.isOriginalResponse = true
        return this.custom(ky.delete, url, options, deleteSettings)
    }

    /**
     * Методи з Store суфіксом, які повертають HTTPResponseStore для реактивності
     */

    /**
     * Виконує GET запит з HTTPResponseStore для реактивного управління станом
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    getStore(url: string, options?: Options, settings?: IHttpRequestSettings): HTTPResponseStore | null {
        return this.customStore(ky.get, url, options, settings)
    }

    /**
     * Виконує POST запит з HTTPResponseStore для реактивного управління станом
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    postStore(url: string, options?: Options, settings?: IHttpRequestSettings): HTTPResponseStore | null {
        return this.customStore(ky.post, url, options, settings)
    }

    /**
     * Виконує PUT запит з HTTPResponseStore для реактивного управління станом
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    putStore(url: string, options?: Options, settings?: IHttpRequestSettings): HTTPResponseStore | null {
        return this.customStore(ky.put, url, options, settings)
    }

    /**
     * Виконує PATCH запит з автоматичним Content-Type і HTTPResponseStore
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    patchStore(url: string, options?: Options, settings?: IHttpRequestSettings): HTTPResponseStore | null {
        const patchOptions = options ?? {}

        // Безпечна робота з headers
        if (!patchOptions.headers) {
            patchOptions.headers = {}
        }

        const headers = patchOptions.headers as Record<string, string>
        if (headers['Content-Type'] === undefined) {
            headers['Content-Type'] = 'application/merge-patch+json'
        }

        return this.customStore(ky.patch, url, patchOptions, settings)
    }

    /**
     * Виконує DELETE запит з відповідними налаштуваннями і HTTPResponseStore
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    deleteStore(url: string, options?: Options, settings?: IHttpRequestSettings): HTTPResponseStore | null {
        const deleteSettings = settings ?? {}
        deleteSettings.isNotHeaderAddContentType = true
        deleteSettings.isOriginalResponse = true
        return this.customStore(ky.delete, url, options, deleteSettings)
    }

    /**
     * Базовий метод для запитів, що повертають Promise з KY client
     * @param method - HTTP метод (ky.get, ky.post, тощо)
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns ResponsePromise з KY client
     */
    custom<T = unknown>(
        method: KyMethod,
        url?: string,
        options: Options = {},
        settings: IHttpRequestSettings = {}
    ): ResponsePromise<T> | null {
        if (typeof method !== 'function' || !url) {
            console.warn('[RestClientService] Invalid method or URL:', { method: typeof method, url })
            return null
        }

        settings.isDisableResponseCache = settings.isDisableResponseCache ?? false

        const fullUrl = this.getFullUrl(url, settings)
        const methodName = this.getMethodName(method)
        const profileId = this.startProfiling(methodName, fullUrl, options, settings)

        // Створюємо копію settings для уникнення мутацій
        const settingsWithProfile: IHttpRequestSettings = {
            ...settings,
            ...(profileId ? { profileId } : {}),
        }

        // Застосування request трансформера якщо потрібно
        let processedOptions = this.applyRequestTransformer(options, settingsWithProfile)

        // Підготовка опцій з трансформером відповіді якщо потрібно
        processedOptions = this.prepareOptionsWithTransformer(processedOptions, settingsWithProfile)

        const buildedOptions = this.fillOptions(processedOptions, settingsWithProfile)

        // Дедуплікація для GET запитів
        if (method === ky.get && settings.isDisableResponseCache === false && !buildedOptions?.signal) {
            const requestKey = this.getRequestKey({ url: fullUrl, options })

            // Якщо forceRefresh = true, очищаємо всі кеші
            if (settings.forceRefresh) {
                this.clearRequestCache(requestKey)
            } else {
                // Перевіряємо sharedPromises кеш
                if (this.sharedPromises.has(requestKey)) {
                    const cachedPromise = this.sharedPromises.get(requestKey)!
                    return cachedPromise as ResponsePromise<T>
                }

                // Створюємо AbortController для можливості відміни
                const abortController = new AbortController()
                buildedOptions.signal = abortController.signal
                this.runningRequest.set(requestKey, abortController)
            }
        }

        // Розумний timeout: 30 секунд за замовчуванням
        buildedOptions.timeout = buildedOptions.timeout ?? 30000

        const responsePromise = method(fullUrl, buildedOptions) as ResponsePromise<T>

        // Для GET запитів зберігаємо промис для дедуплікації
        if (method === ky.get && settings.isDisableResponseCache === false && !settings.forceRefresh) {
            const requestKey = this.getRequestKey({ url: fullUrl, options })
            
            let sharedPromise: Promise<any>

            if (!settingsWithProfile.isOriginalResponse && typeof responsePromise.json === 'function') {
                sharedPromise = this.handleJsonResponse(responsePromise, profileId)
            } else {
                sharedPromise = responsePromise as any
            }

            // Зберігаємо промис в sharedPromises
            this.sharedPromises.set(requestKey, sharedPromise)

            // Очистка після завершення
            sharedPromise
                .then((data) => {
                    this.runningRequest.delete(requestKey)
                    // Автоочистка через 30 секунд
                    setTimeout(() => this.sharedPromises.delete(requestKey), 30000)
                    return data
                })
                .catch((error) => {
                    // Очищаємо кеш при помилці
                    this.clearRequestCache(requestKey)
                    // Не перекидаємо помилку тут: вона вже доступна через sharedPromise безпосередньо.
                    // Повторне кидання в side-effect ланцюжку створює "Uncaught (in promise)".
                    void error
                })

            return sharedPromise as any
        }

        // Профілювання помилок
        if (profileId) {
            responsePromise.catch((error: Error) => {
                requestProfiler.endRequestWithError(profileId, error)
            })
        }

        // Автоматично парсимо JSON якщо не потрібна оригінальна відповідь
        if (!settingsWithProfile.isOriginalResponse && typeof responsePromise.json === 'function') {
            return this.handleJsonResponse(responsePromise, profileId) as any
        }

        return responsePromise
    }

    /**
     * Обробка JSON відповіді з правильною обробкою помилок
     */
    private handleJsonResponse(responsePromise: ResponsePromise, profileId?: string): Promise<any> {
        return responsePromise
            .then(async (response) => {
                const status = response.status

                // Статуси без тіла відповіді
                if (status === 204 || status === 205) {
                    if (profileId) {
                        requestProfiler.endRequest(profileId, response)
                    }
                    return null
                }

                const data = await response.json()
                
                if (profileId) {
                    requestProfiler.endRequest(profileId, response, data)
                }
                
                return data
            })
            .catch(async (error: any) => {
                // Обробка HTTPError з ky
                if (error.response && typeof error.response.json === 'function') {
                    let enhancedError: any = null
                    try {
                        const errorData = await error.response.json()
                        enhancedError = new Error(error.message) as any
                        
                        // Копіюємо властивості
                        Object.keys(error).forEach((key) => {
                            if (key !== 'response') {
                                enhancedError[key] = error[key]
                            }
                        })
                        
                        enhancedError.response = errorData
                        // Зберігаємо HTTP статус-код окремо для зручної обробки в catch-блоках
                        enhancedError.statusCode = error.response.status
                    } catch (parseError) {
                        console.error('[RestClientService] Failed to parse error response:', parseError)
                    }
                    // throw знаходиться ПОЗА try, щоб не перехопити себе ж
                    throw enhancedError ?? error
                }
                throw error
            })
    }

    /**
     * Очищення всіх кешів для конкретного запиту
     */
    private clearRequestCache(requestKey: string): void {
        // Відміна активного запиту
        if (this.runningRequest.has(requestKey)) {
            const abortController = this.runningRequest.get(requestKey)
            abortController?.abort()
            this.runningRequest.delete(requestKey)
        }
        
        // Очистка всіх кешів
        this.cacheResponse.delete(requestKey)
        this.pendingRequests.delete(requestKey)
        this.sharedPromises.delete(requestKey)
    }

    /**
     * Базовий метод для запитів, що повертають HTTPResponseStore з реактивністю
     * @param method - HTTP метод (ky.get, ky.post, тощо)
     * @param url - URL для запиту
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю та кешуванням
     */
    customStore(
        method: KyMethod,
        url?: string,
        options: Options = {},
        settings: IHttpRequestSettings = {}
    ): HTTPResponseStore | null {
        if (typeof method !== 'function' || !url) {
            console.warn('[RestClientService] Invalid method or URL:', { method: typeof method, url })
            return null
        }

        settings.isDisableResponseCache = settings.isDisableResponseCache ?? false

        const fullUrl = this.getFullUrl(url, settings)
        const methodName = this.getMethodName(method)
        const profileId = this.startProfiling(methodName, fullUrl, options, settings)

        // Створюємо копію settings для уникнення мутацій
        const settingsWithProfile: IHttpRequestSettings = {
            ...settings,
            ...(profileId ? { profileId } : {}),
        }

        // Застосування request трансформера якщо потрібно
        let processedOptions = this.applyRequestTransformer(options, settingsWithProfile)

        // Підготовка опцій з трансформером відповіді якщо потрібно
        processedOptions = this.prepareOptionsWithTransformer(processedOptions, settingsWithProfile)

        const buildedOptions = this.fillOptions(processedOptions, settingsWithProfile)

        // Дедуплікація та кешування тільки для GET запитів
        if (method === ky.get && settings.isDisableResponseCache === false && !buildedOptions?.signal) {
            const requestKey = this.getRequestKey({ url: fullUrl, options, settings })

            // Якщо forceRefresh = true, очищаємо всі існуючі запити та кеш
            if (settings.forceRefresh) {
                this.clearRequestCache(requestKey)
            } else {
                // Перевіряємо кеш - якщо є завершений запит, повертаємо його
                if (this.cacheResponse.has(requestKey)) {
                    const cachedData = this.cacheResponse.get(requestKey)
                    return cachedData || null
                }

                // Перевіряємо активні запити - якщо є запит в процесі, повертаємо той же Store
                // Це ключовий момент дедупликації: всі паралельні запити отримують один і той же Store
                if (this.pendingRequests.has(requestKey)) {
                    const pendingStore = this.pendingRequests.get(requestKey)
                    return pendingStore || null
                }
            }

            // Створюємо AbortController для можливості відміни запиту
            const abortController = new AbortController()
            buildedOptions.signal = abortController.signal
            this.runningRequest.set(requestKey, abortController)
        }

        // Розумний timeout: 30 секунд за замовчуванням
        buildedOptions.timeout = buildedOptions.timeout ?? 30000

        const response = method(fullUrl, buildedOptions)

        // Профілювання помилок
        if (profileId) {
            response.catch((error: Error) => {
                requestProfiler.endRequestWithError(profileId, error)
            })
        }

        const HTTPResponseInstance = new HTTPResponseStore({
            response,
            requestClient: this,
            method,
            requestData: { url: fullUrl, options, settings },
            isOriginalResponse: settings.isOriginalResponse || false,
        })

        // Для GET запитів без forceRefresh зберігаємо Store в pendingRequests
        // Це забезпечує дедупликацію паралельних запитів
        if (method === ky.get && settings.isDisableResponseCache === false && !settings.forceRefresh) {
            const requestKey = this.getRequestKey({ url: fullUrl, options, settings })
            this.pendingRequests.set(requestKey, HTTPResponseInstance)
        }

        return HTTPResponseInstance
    }

    getRequestKey({
        url = '',
        options = {},
    }: {
        url?: string
        options?: Options
        settings?: IHttpRequestSettings
    }): string {
        return JSON.stringify({
            url,
            searchParams: options.searchParams ?? {},
            headers: options.headers ?? {},
        })
    }
}

/**
 * Ленивая ініціалізація RestClientService
 * Створює екземпляр тільки коли він потрібен, забезпечуючи що конфігурація вже завантажена
 */
let _restClient: RestClientService | null = null

function createRestClient(): RestClientService {
    // Спробуємо отримати AppConfig з configManager
    const appConfig = configManager.get('AppConfig') as any
    const backendUrl = appConfig?.backendUrl || ''

    return new RestClientService({ backendUrl })
}

export const restClient = new Proxy({} as RestClientService, {
    get(_target, prop: string | symbol) {
        // Ленива ініціалізація при першому зверненні
        if (!_restClient) {
            _restClient = createRestClient()
        }

        const value = _restClient[prop as keyof RestClientService]

        // Якщо це метод - прив'язуємо контекст
        if (typeof value === 'function') {
            return (value as Function).bind(_restClient)
        }

        return value
    },
    set(_target, prop: string | symbol, value: any) {
        // Ленива ініціалізація при першому зверненні
        if (!_restClient) {
            _restClient = createRestClient()
        }

        (_restClient as any)[prop] = value
        return true
    },
})
